import React, { useState } from 'react';
import { ScheduledCommandPanelModelProps } from './modelTypes';
import {
  badgeClass,
  fmtDateTime,
  isOfferAwaitingClient,
  normalizePassengerRows,
  requestDisplayStatus,
  statusTone,
} from './utils';
import { FullContactFormModal } from '../ContactsHub';
import type { Customer } from '../../types';

const ScheduledCommandPanelModals: React.FC<ScheduledCommandPanelModelProps> = ({ model }) => {
  const [showQuickCreate, setShowQuickCreate] = useState(false);
  const [localCustomers, setLocalCustomers] = useState<Customer[]>([]);
  const {
    bookingThread,
    bookingThreadError,
    bookingThreadLoading,
    cancelPreview,
    cancelReason,
    confirmCancelTicket,
    canHardDeleteBooking,
    customerDiscountPercent,
    customerDiscountSource,
    customers,
    deleteConfirmText,
    deleteReason,
    hardDeleteTicket,
    isDeletingBooking,
    isIssuingTicket,
    isLoadingTicketCustomer,
    isPreparingCancelPreview,
    isConfirmingCancel,
    isSendingBookingEmail,
    isSendingBookingWhatsapp,
    isSendingReviewOffer,
    operatorDiscountCap,
    openBookingDetail,
    openCancelTicketConfirm,
    reviewClientName,
    reviewClientPhone,
    reviewCustomerDiscountPercent,
    reviewMessage,
    reviewOperatorDiscountPercent,
    reviewPricing,
    reviewRequest,
    reviewSeatPriceUSD,
    reviewSeats,
    selectedBooking,
    selectedPlan,
    sendBookingEmail,
    sendBookingWhatsapp,
    sendOfferFromReview,
    setDeleteConfirmText,
    setDeleteReason,
    setCancelReason,
    setReviewClientName,
    setReviewClientPhone,
    setReviewCustomerDiscountPercent,
    setReviewMessage,
    setReviewOperatorDiscountPercent,
    setReviewRequest,
    setReviewSeatPriceUSD,
    setReviewSeats,
    setSelectedBooking,
    setShowCancelConfirm,
    setSelectedPlan,
    setShowDeleteConfirm,
    setTicketForm,
    setTicketMode,
    setTicketPassengers,
    setTicketPlan,
    showDeleteConfirm,
    showCancelConfirm,
    submitIssueTicket,
    ticketBookingWindow,
    ticketForm,
    ticketMode,
    ticketPassengers,
    ticketPlan,
    ticketPricing,
    updateTicketStatus,
  } = model;
  const mergedCustomers = [...localCustomers, ...customers];
  const selectedTicketCustomer =
    ticketForm.selectedCustomerId && ticketForm.selectedCustomerId !== 'MANUAL'
      ? mergedCustomers.find((c) => c.id === ticketForm.selectedCustomerId) || null
      : null;
  const ticketIdentityLinked = !!selectedTicketCustomer;
  const ticketZohoLinked = !!String((selectedTicketCustomer as any)?.zohoContactId || '').trim();

  return (
    <>
      {reviewRequest && (
        <div className="fixed inset-0 z-[118] bg-slate-900/45 backdrop-blur-sm p-4 flex items-center justify-center">
          <div className="w-full max-w-2xl rounded-2xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-[linear-gradient(135deg,#0b1630_0%,#14315f_46%,#0a4b7a_100%)] text-white">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.14em] text-cyan-200/80">Offer Review</p>
                <h4 className="text-base font-black tracking-tight">
                  {reviewRequest.fromCode || reviewRequest.matchedInventory?.fromCode || '—'} ➔ {reviewRequest.toCode || reviewRequest.matchedInventory?.toCode || '—'}
                </h4>
              </div>
              <button onClick={() => setReviewRequest(null)} className="rounded-lg border border-white/30 bg-white/10 px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-white">Close</button>
            </div>
            <div className="p-4 space-y-3">
              <div className="rounded-xl border border-sky-200 bg-sky-50 px-3 py-2 text-xs font-semibold text-sky-800">
                Client: {reviewClientName || reviewRequest.customerName || reviewRequest.fromEmail || 'Unknown'} · PAX: {reviewRequest.pax || reviewSeats} · Departure: {fmtDateTime(reviewRequest.matchedInventory?.departureAt || reviewRequest.travelDate)}
              </div>
              {!String(reviewRequest.inboxMessageId || '').trim() && (
                <div className="rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-700">
                  This request is not linked to an inbound email thread. Link it from Inbound Hub before sending offer.
                </div>
              )}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="mb-1 block text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Client Name</label>
                  <input
                    value={reviewClientName}
                    onChange={(e) => setReviewClientName(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                    placeholder="Client name"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Client Number</label>
                  <input
                    value={reviewClientPhone}
                    onChange={(e) => setReviewClientPhone(e.target.value)}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                    placeholder="+255..."
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="mb-1 block text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Seats</label>
                  <input
                    type="number"
                    min={1}
                    value={reviewSeats}
                    onChange={(e) => setReviewSeats(Math.max(1, Number(e.target.value) || 1))}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Unit Price (USD)</label>
                  <input
                    type="number"
                    min={0}
                    value={reviewSeatPriceUSD}
                    onChange={(e) => setReviewSeatPriceUSD(Math.max(0, Number(e.target.value) || 0))}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="mb-1 block text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Customer Discount %</label>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    value={reviewCustomerDiscountPercent}
                    onChange={(e) => setReviewCustomerDiscountPercent(Math.max(0, Math.min(100, Number(e.target.value) || 0)))}
                    className="w-full rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Operator Discount % (Cap {operatorDiscountCap || 0})</label>
                  <input
                    type="number"
                    min={0}
                    max={Number(operatorDiscountCap || 0)}
                    value={reviewOperatorDiscountPercent}
                    onChange={(e) => setReviewOperatorDiscountPercent(Math.max(0, Math.min(Number(operatorDiscountCap || 0), Number(e.target.value) || 0)))}
                    className="w-full rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-800"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"><p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Rack Total</p><p className="mt-1 font-black text-slate-900">USD {reviewPricing.rackTotal.toFixed(2)}</p></div>
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2"><p className="text-[10px] font-black uppercase tracking-[0.12em] text-emerald-600">Cust Disc</p><p className="mt-1 font-black text-emerald-800">-{reviewPricing.customerDiscountAmount.toFixed(2)}</p></div>
                <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2"><p className="text-[10px] font-black uppercase tracking-[0.12em] text-indigo-600">Ops Disc</p><p className="mt-1 font-black text-indigo-800">-{reviewPricing.operatorDiscountAmount.toFixed(2)}</p></div>
                <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2"><p className="text-[10px] font-black uppercase tracking-[0.12em] text-sky-600">Net Total</p><p className="mt-1 font-black text-sky-800">USD {reviewPricing.netTotal.toFixed(2)}</p></div>
              </div>
              <div>
                <label className="mb-1 block text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Offer Message</label>
                <textarea
                  rows={6}
                  value={reviewMessage}
                  onChange={(e) => setReviewMessage(e.target.value)}
                  className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                />
              </div>
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setReviewRequest(null)}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-[10px] font-black uppercase tracking-[0.12em] text-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => void sendOfferFromReview()}
                  disabled={
                    isSendingReviewOffer ||
                    isOfferAwaitingClient(requestDisplayStatus(reviewRequest)) ||
                    !String(reviewRequest.inboxMessageId || '').trim()
                  }
                  className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-[10px] font-black uppercase tracking-[0.12em] text-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {isOfferAwaitingClient(requestDisplayStatus(reviewRequest))
                    ? 'Already Sent'
                    : !String(reviewRequest.inboxMessageId || '').trim()
                      ? 'No Thread Linked'
                      : (isSendingReviewOffer ? 'Sending…' : 'Send Offer')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedPlan && (
        <div className="fixed inset-0 z-[120] bg-slate-900/45 backdrop-blur-sm p-4 flex items-center justify-center">
          <div className="w-full max-w-2xl rounded-2xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-500">Seat Plan Detail</p>
                <h4 className="text-base font-black text-slate-900">{selectedPlan.fromCode} ➔ {selectedPlan.toCode}</h4>
              </div>
              <button onClick={() => setSelectedPlan(null)} className="rounded-lg border border-slate-300 px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-slate-700">Close</button>
            </div>
            <div className="p-4 space-y-3 max-h-[70vh] overflow-auto">
              <div className="text-xs font-semibold text-slate-600">
                Departure: {fmtDateTime(selectedPlan.departureAt)} | Price: {selectedPlan.currencyCode} {selectedPlan.seatPriceUSD}
              </div>
              <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-500">Booking Details</p>
              {selectedPlan.bookings.length ? (
                <div className="space-y-2">
                  {selectedPlan.bookings.map((b) => (
                    <button
                      type="button"
                      key={b.id}
                      onClick={() => openBookingDetail(selectedPlan, b)}
                      className="w-full text-left rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs hover:bg-indigo-50"
                    >
                      <div className="flex flex-wrap items-center gap-2">
                        <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.1em] ${badgeClass(statusTone(b.status))}`}>{b.status}</span>
                        <span className="font-bold text-slate-800">{b.customerName || b.customerEmail || 'Unknown'}</span>
                        <span className="text-slate-500">Seats: {b.seats}</span>
                        {b.quoteId && <span className="text-slate-500">Quote: {b.quoteId}</span>}
                        {(() => {
                          const pax = normalizePassengerRows((b as any)?.metadata || {});
                          if (!pax.length) return null;
                          return <span className="text-slate-500">Passengers: {pax.length}</span>;
                        })()}
                        <span className="ml-auto text-[10px] font-black uppercase tracking-[0.12em] text-indigo-600">Open Ticket</span>
                      </div>
                      <div className="mt-1 text-slate-500">
                        {fmtDateTime(b.createdAt)} {b.createdByName ? `• by ${b.createdByName}` : ''}
                      </div>
                      {b.notes && <div className="mt-1 text-slate-600">{b.notes}</div>}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-xs font-semibold text-slate-500">No booking details yet for this slot.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {selectedBooking && (
        <div
          className="fixed inset-0 z-[125] bg-slate-900/45 backdrop-blur-sm"
          onClick={() => setSelectedBooking(null)}
        >
          <aside
            className="ml-auto h-full w-full max-w-3xl border-l border-slate-200 bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex h-full flex-col">
              <div className="border-b border-slate-200 bg-[linear-gradient(135deg,#0b1630_0%,#14315f_46%,#0a4b7a_100%)] px-5 py-4 text-white">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.16em] text-cyan-200/80">Ticket Detail</p>
                    <h4 className="text-xl font-black tracking-tight">{selectedBooking.routeFrom} ➔ {selectedBooking.routeTo}</h4>
                    <p className="mt-1 text-xs font-semibold text-cyan-100/90">
                      Departure {fmtDateTime(selectedBooking.departureAt)} • Status {String(selectedBooking.status || 'UNKNOWN').toUpperCase()}
                    </p>
                  </div>
                  <button
                    onClick={() => setSelectedBooking(null)}
                    className="rounded-lg border border-white/25 bg-white/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.12em] text-white"
                  >
                    Close
                  </button>
                </div>
              </div>

              <div className="flex-1 overflow-auto p-5 space-y-4">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"><p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Status</p><p className="mt-1 font-black text-slate-900">{selectedBooking.status}</p></div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"><p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Seats</p><p className="mt-1 font-black text-slate-900">{selectedBooking.seats}</p></div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"><p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Issued At</p><p className="mt-1 font-black text-slate-900">{fmtDateTime(selectedBooking.createdAt)}</p></div>
                  <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"><p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Issued By</p><p className="mt-1 font-black text-slate-900">{selectedBooking.createdByName || '—'}</p></div>
                </div>
                {(() => {
                  const comm = (selectedBooking?.metadata?.communication || {}) as any;
                  const lastEmail = comm?.lastEmail;
                  const lastWhatsapp = comm?.lastWhatsapp;
                  if (!lastEmail && !lastWhatsapp) return null;
                  return (
                    <div className="flex flex-wrap gap-2">
                      {lastEmail && (
                        <span className="inline-flex rounded-full border border-sky-200 bg-sky-50 px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-sky-700">
                          Email: {fmtDateTime(lastEmail.at)}
                        </span>
                      )}
                      {lastWhatsapp && (
                        <span className="inline-flex rounded-full border border-emerald-200 bg-emerald-50 px-2 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-emerald-700">
                          WhatsApp: {fmtDateTime(lastWhatsapp.at)}
                        </span>
                      )}
                    </div>
                  );
                })()}

                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-500">Email Thread</p>
                    {bookingThread?.summary && (
                      <div className="flex flex-wrap gap-2">
                        <span
                          className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] ${
                            bookingThread.summary.sent
                              ? 'border-sky-200 bg-sky-50 text-sky-700'
                              : 'border-slate-200 bg-slate-50 text-slate-500'
                          }`}
                        >
                          {bookingThread.summary.sent ? 'Email: Sent' : 'Email: Not Sent'}
                        </span>
                        <span
                          className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] ${
                            bookingThread.summary.replied
                              ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                              : 'border-amber-200 bg-amber-50 text-amber-700'
                          }`}
                        >
                          {bookingThread.summary.replied ? 'Thread: Client Replied' : 'Thread: Awaiting Reply'}
                        </span>
                      </div>
                    )}
                  </div>

                  {bookingThreadLoading ? (
                    <p className="mt-2 text-xs font-semibold text-slate-500">Loading thread…</p>
                  ) : bookingThreadError ? (
                    <p className="mt-2 text-xs font-semibold text-rose-600">{bookingThreadError}</p>
                  ) : (bookingThread?.messages || []).length === 0 ? (
                    <p className="mt-2 text-xs font-semibold text-slate-500">No thread messages yet for this ticket.</p>
                  ) : (
                    <div className="mt-3 max-h-64 space-y-2 overflow-auto pr-1">
                      {(bookingThread?.messages || []).map((m: any) => {
                        const dir = String(m?.direction || '').toUpperCase();
                        const isInbound = dir === 'INBOUND';
                        const body =
                          String(m?.textBody || '').trim() ||
                          String(m?.htmlBody || '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim() ||
                          '(no content)';
                        return (
                          <div
                            key={m.id}
                            className={`rounded-lg border px-3 py-2 text-xs ${
                              isInbound ? 'border-emerald-200 bg-emerald-50' : 'border-indigo-200 bg-indigo-50'
                            }`}
                          >
                            <div className="flex flex-wrap items-center gap-2">
                              <span
                                className={`inline-flex rounded-full border px-2 py-0.5 text-[9px] font-black uppercase tracking-[0.1em] ${
                                  isInbound
                                    ? 'border-emerald-300 bg-emerald-100 text-emerald-700'
                                    : 'border-indigo-300 bg-indigo-100 text-indigo-700'
                                }`}
                              >
                                {dir || 'MESSAGE'}
                              </span>
                              <span className="text-[11px] font-black text-slate-700">
                                {fmtDateTime(m?.receivedAt || m?.sentAt || m?.createdAt)}
                              </span>
                              <span className="text-[11px] text-slate-500">
                                {m?.fromEmail || '—'} {m?.toEmails ? `→ ${String(m.toEmails).split(',')[0]}` : ''}
                              </span>
                            </div>
                            <p className="mt-1 font-black text-slate-800">{m?.subject || 'No subject'}</p>
                            <p className="mt-1 whitespace-pre-wrap text-slate-700">{body}</p>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-500 mb-2">Customer</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"><span className="font-black text-slate-500">Name:</span> <span className="font-black text-slate-800">{selectedBooking.customerName || '—'}</span></div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"><span className="font-black text-slate-500">Email:</span> <span className="font-black text-slate-800">{selectedBooking.customerEmail || '—'}</span></div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"><span className="font-black text-slate-500">Customer ID:</span> <span className="font-black text-slate-800">{selectedBooking.customerId || '—'}</span></div>
                    <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"><span className="font-black text-slate-500">Quote:</span> <span className="font-black text-slate-800">{selectedBooking.quoteId || '—'}</span></div>
                    <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2"><span className="font-black text-indigo-600">Booking:</span> <span className="font-black text-indigo-800">{(selectedBooking as any).bookingId || (selectedBooking as any)?.metadata?.bookingId || '—'}</span></div>
                    <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2"><span className="font-black text-indigo-600">PNR:</span> <span className="font-black text-indigo-800">{(selectedBooking as any).pnrCode || (selectedBooking as any)?.metadata?.pnrCode || '—'}</span></div>
                  </div>
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-500 mb-2">Fare Breakdown</p>
                  {(() => {
                    const discountMeta = (selectedBooking?.metadata?.discount || {}) as any;
                    const seats = Math.max(1, Number(selectedBooking.seats || 1));
                    const unit = Number(selectedBooking.unitPriceUSD || 0);
                    const total = Number(selectedBooking.totalPriceUSD || 0);
                    const customerPercent = Number(discountMeta.customerPercent || 0);
                    const operatorPercent = Number(discountMeta.operatorPercent || 0);
                    const customerAmount = Number(discountMeta.customerAmount || 0);
                    const operatorAmount = Number(discountMeta.operatorAmount || 0);
                    const rackTotal = customerAmount || operatorAmount ? total + customerAmount + operatorAmount : total;
                    const rackUnit = seats > 0 ? rackTotal / seats : unit;
                    return (
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"><p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Rack Unit</p><p className="mt-1 font-black text-slate-900">{selectedBooking.currencyCode || 'USD'} {rackUnit.toFixed(2)}</p></div>
                        <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2"><p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Rack Total</p><p className="mt-1 font-black text-slate-900">{selectedBooking.currencyCode || 'USD'} {rackTotal.toFixed(2)}</p></div>
                        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2"><p className="text-[10px] font-black uppercase tracking-[0.12em] text-emerald-600">Customer Disc</p><p className="mt-1 font-black text-emerald-800">{customerPercent}% (-{customerAmount.toFixed(2)})</p></div>
                        <div className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2"><p className="text-[10px] font-black uppercase tracking-[0.12em] text-indigo-600">Operator Disc</p><p className="mt-1 font-black text-indigo-800">{operatorPercent}% (-{operatorAmount.toFixed(2)})</p></div>
                        <div className="md:col-span-2 rounded-lg border border-sky-200 bg-sky-50 px-3 py-2"><p className="text-[10px] font-black uppercase tracking-[0.12em] text-sky-600">Net Unit</p><p className="mt-1 font-black text-sky-800">{selectedBooking.currencyCode || 'USD'} {unit.toFixed(2)}</p></div>
                        <div className="md:col-span-2 rounded-lg border border-slate-200 bg-slate-100 px-3 py-2"><p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-600">Net Total</p><p className="mt-1 font-black text-slate-900">{selectedBooking.currencyCode || 'USD'} {total.toFixed(2)}</p></div>
                      </div>
                    );
                  })()}
                </div>

                {(() => {
                  const cancellation = (selectedBooking?.metadata?.cancellation?.latest || null) as any;
                  if (!cancellation) return null;
                  const currency = String(cancellation?.currencyCode || selectedBooking?.currencyCode || 'USD').toUpperCase();
                  const penaltyPercent = Number(cancellation?.penaltyPercent || 0);
                  const penaltyAmount = Number(cancellation?.penaltyAmount || 0);
                  const refundableAmount = Number(cancellation?.refundableAmount || 0);
                  const hoursBefore = cancellation?.hoursBeforeDeparture;
                  return (
                    <div className="rounded-xl border border-rose-200 bg-rose-50 p-4">
                      <p className="text-[10px] font-black uppercase tracking-[0.14em] text-rose-600 mb-2">Cancellation Policy Charge</p>
                      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 text-xs">
                        <div className="rounded-lg border border-rose-200 bg-white px-3 py-2">
                          <p className="text-[10px] font-black uppercase tracking-[0.12em] text-rose-500">Fee</p>
                          <p className="mt-1 font-black text-rose-800">{penaltyPercent}%</p>
                        </div>
                        <div className="rounded-lg border border-rose-200 bg-white px-3 py-2">
                          <p className="text-[10px] font-black uppercase tracking-[0.12em] text-rose-500">Penalty</p>
                          <p className="mt-1 font-black text-rose-800">{currency} {penaltyAmount.toFixed(2)}</p>
                        </div>
                        <div className="rounded-lg border border-emerald-200 bg-white px-3 py-2">
                          <p className="text-[10px] font-black uppercase tracking-[0.12em] text-emerald-600">Refundable</p>
                          <p className="mt-1 font-black text-emerald-800">{currency} {refundableAmount.toFixed(2)}</p>
                        </div>
                        <div className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                          <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Window</p>
                          <p className="mt-1 font-black text-slate-800">
                            {typeof hoursBefore === 'number' && Number.isFinite(hoursBefore)
                              ? `${hoursBefore.toFixed(1)}h before dep`
                              : 'Policy window'}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })()}

                {selectedBooking.notes && (
                  <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-700 whitespace-pre-wrap">
                    {selectedBooking.notes}
                  </div>
                )}

                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-500 mb-2">Audit Timeline</p>
                  {(() => {
                    const trail = Array.isArray(selectedBooking?.metadata?.auditTrail)
                      ? selectedBooking.metadata.auditTrail
                      : [];
                    const fallback = [{
                      at: selectedBooking.createdAt,
                      action: 'TICKET_CREATED',
                      byName: selectedBooking.createdByName || '—',
                      fromStatus: null,
                      toStatus: selectedBooking.status,
                      reason: null,
                    }];
                    const rows = (trail.length ? trail : fallback).slice().reverse();
                    return (
                      <div className="space-y-2 text-xs">
                        {rows.map((row: any, idx: number) => (
                          <div key={`audit-${idx}`} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                            <p className="font-black text-slate-800">{String(row?.action || 'UPDATE').replace(/_/g, ' ')}</p>
                            <p className="text-slate-600">
                              {fmtDateTime(row?.at)}{row?.byName ? ` • by ${row.byName}` : ''}
                            </p>
                            {(row?.fromStatus || row?.toStatus) && (
                              <p className="text-slate-500">
                                {row?.fromStatus ? `${row.fromStatus} -> ` : ''}{row?.toStatus || '—'}
                              </p>
                            )}
                            {row?.reason && <p className="text-slate-500">{String(row.reason)}</p>}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>

                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-500 mb-2">Passenger Manifest</p>
                  {(() => {
                    const manifestRows = normalizePassengerRows(selectedBooking?.metadata || {});
                    if (!manifestRows.length) {
                      return <div className="text-xs font-semibold text-slate-500">No passenger manifest recorded.</div>;
                    }
                    return (
                    <div className="space-y-2">
                      {manifestRows.map((p: any, idx: number) => (
                        <div key={`ticket-pax-${idx + 1}`} className="grid grid-cols-1 md:grid-cols-[100px_1fr_220px] gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs">
                          <div className="font-black text-slate-700">PAX {p?.index || idx + 1}</div>
                          <div className="font-semibold text-slate-800">{String(p?.name || '—')}</div>
                          <div className="text-slate-600">{String(p?.idNumber || 'No ID')}</div>
                        </div>
                      ))}
                    </div>
                    );
                  })()}
                </div>
              </div>

              <div className="border-t border-slate-200 bg-slate-50 px-5 py-3">
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={sendBookingEmail}
                    disabled={isSendingBookingEmail}
                    className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.12em] text-sky-700 disabled:opacity-60"
                  >
                    {isSendingBookingEmail ? 'Sending Email...' : 'Send Email'}
                  </button>
                  <button
                    type="button"
                    onClick={sendBookingWhatsapp}
                    disabled={isSendingBookingWhatsapp}
                    className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.12em] text-emerald-700 disabled:opacity-60"
                  >
                    {isSendingBookingWhatsapp ? 'Preparing WhatsApp...' : 'Send WhatsApp'}
                  </button>
                  {String(selectedBooking.status || '').toUpperCase() === 'HELD' && (
                    <button
                      type="button"
                      onClick={() => updateTicketStatus(selectedBooking.id, 'CONFIRMED')}
                      className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.12em] text-emerald-700"
                    >
                      Convert To Confirmed
                    </button>
                  )}
                  {['HELD', 'CONFIRMED'].includes(String(selectedBooking.status || '').toUpperCase()) && (
                    <button
                      type="button"
                      onClick={() => void openCancelTicketConfirm(selectedBooking.id)}
                      disabled={isPreparingCancelPreview || isConfirmingCancel}
                      className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.12em] text-rose-700 disabled:opacity-60"
                    >
                      {isPreparingCancelPreview ? 'Loading Policy...' : 'Cancel Ticket'}
                    </button>
                  )}
                  {canHardDeleteBooking && (
                    <button
                      type="button"
                      onClick={() => {
                        setShowDeleteConfirm(true);
                        setDeleteReason('');
                        setDeleteConfirmText('');
                      }}
                      disabled={isDeletingBooking}
                      className="rounded-lg border border-rose-300 bg-rose-600 px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.12em] text-white disabled:opacity-60"
                    >
                      {isDeletingBooking ? 'Deleting...' : 'Delete Permanently'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </aside>
        </div>
      )}

      {showDeleteConfirm && selectedBooking && (
        <div className="fixed inset-0 z-[160] bg-slate-900/50 backdrop-blur-sm p-4 flex items-center justify-center">
          <div className="w-full max-w-lg rounded-[28px] border border-rose-200/80 bg-gradient-to-b from-white to-rose-50/40 shadow-[0_30px_80px_rgba(15,23,42,0.32)] overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-rose-200/70 bg-[linear-gradient(135deg,#3f1221_0%,#7f1d1d_42%,#be123c_100%)]">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.14em] text-rose-100/80">Admin Action</p>
                <h4 className="text-lg font-black text-white">Delete Ticket Permanently</h4>
              </div>
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="rounded-full border border-white/40 bg-white/10 px-3.5 py-1.5 text-[10px] font-black uppercase tracking-[0.12em] text-white transition hover:bg-white/20"
              >
                Close
              </button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm font-semibold text-slate-700">
                This will permanently delete the ticket record and cannot be undone.
              </p>
              <div className="rounded-2xl border border-rose-300/70 bg-gradient-to-b from-rose-100 to-rose-50 px-3 py-2 text-xs text-rose-800 shadow-[0_8px_16px_rgba(244,63,94,0.14)]">
                Booking ID: <span className="font-black">{selectedBooking.id}</span>
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Type DELETE to confirm</label>
                <input
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  className="w-full rounded-2xl border border-slate-300 bg-gradient-to-b from-white to-slate-50 px-3 py-2.5 text-sm shadow-[inset_0_1px_0_rgba(255,255,255,0.95),0_6px_12px_rgba(15,23,42,0.06)] outline-none focus:border-rose-300 focus:ring-2 focus:ring-rose-100"
                  placeholder="DELETE"
                />
              </div>
              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Delete reason</label>
                <textarea
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  className="w-full min-h-[90px] rounded-2xl border border-slate-300 bg-gradient-to-b from-white to-slate-50 px-3 py-2.5 text-sm shadow-[inset_0_1px_0_rgba(255,255,255,0.95),0_6px_12px_rgba(15,23,42,0.06)] outline-none focus:border-rose-300 focus:ring-2 focus:ring-rose-100"
                  placeholder="Reason for permanent deletion (required)"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 bg-slate-50/80">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="rounded-full border border-slate-300 bg-white px-4 py-2 text-[10px] font-black uppercase tracking-[0.12em] text-slate-700 shadow-sm transition hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={hardDeleteTicket}
                disabled={isDeletingBooking}
                className="rounded-full border border-rose-300/80 bg-gradient-to-b from-rose-500 to-rose-600 px-4 py-2 text-[10px] font-black uppercase tracking-[0.12em] text-white shadow-[0_10px_20px_rgba(244,63,94,0.28)] transition hover:brightness-105 disabled:opacity-60"
              >
                {isDeletingBooking ? 'Deleting...' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showCancelConfirm && selectedBooking && (
        <div className="fixed inset-0 z-[155] bg-slate-900/50 backdrop-blur-sm p-4 flex items-center justify-center">
          <div className="w-full max-w-2xl rounded-[28px] border border-rose-200/80 bg-gradient-to-b from-white to-rose-50/40 shadow-[0_30px_80px_rgba(15,23,42,0.32)] overflow-hidden">
            <div className="flex items-center justify-between px-5 py-4 border-b border-rose-200/70 bg-[linear-gradient(135deg,#3f1221_0%,#7f1d1d_42%,#be123c_100%)]">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.14em] text-rose-100/80">Cancellation Confirmation</p>
                <h4 className="text-lg font-black text-white">Apply Scheduled Policy and Cancel Ticket</h4>
              </div>
              <button
                type="button"
                onClick={() => {
                  setShowCancelConfirm(false);
                  setCancelReason('');
                }}
                className="rounded-full border border-white/40 bg-white/10 px-3.5 py-1.5 text-[10px] font-black uppercase tracking-[0.12em] text-white transition hover:bg-white/20"
              >
                Close
              </button>
            </div>
            <div className="p-5 space-y-3">
              {(() => {
                const p = (cancelPreview?.preview || {}) as any;
                const policy = (cancelPreview?.policy || {}) as any;
                const currency = String(p?.currencyCode || selectedBooking?.currencyCode || 'USD').toUpperCase();
                const penaltyPercent = Number(p?.penaltyPercent || 0);
                const penaltyAmount = Number(p?.penaltyAmount || 0);
                const refundableAmount = Number(p?.refundableAmount || 0);
                const windowText = typeof p?.hoursBeforeDeparture === 'number' && Number.isFinite(p?.hoursBeforeDeparture)
                  ? `${p.hoursBeforeDeparture.toFixed(1)}h before departure`
                  : 'Policy window';
                return (
                  <>
                    <div className="rounded-2xl border border-slate-200 bg-gradient-to-b from-slate-100 to-white px-3 py-2 text-xs text-slate-700 shadow-[inset_0_1px_0_rgba(255,255,255,0.95)]">
                      Ticket: <span className="font-black">{selectedBooking.id}</span> • Route: <span className="font-black">{selectedBooking.routeFrom} ➔ {selectedBooking.routeTo}</span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                      <div className="rounded-2xl border border-slate-200 bg-gradient-to-b from-white to-slate-50 px-3 py-2 shadow-[0_6px_12px_rgba(15,23,42,0.06)]">
                        <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Free Window</p>
                        <p className="mt-1 font-black text-slate-900">{Number(policy?.freeCancelHours || 0)}h</p>
                      </div>
                      <div className="rounded-2xl border border-rose-300/80 bg-gradient-to-b from-rose-100 to-rose-50 px-3 py-2 shadow-[0_8px_14px_rgba(244,63,94,0.16)]">
                        <p className="text-[10px] font-black uppercase tracking-[0.12em] text-rose-600">Fee</p>
                        <p className="mt-1 font-black text-rose-800">{penaltyPercent}%</p>
                      </div>
                      <div className="rounded-2xl border border-rose-300/80 bg-gradient-to-b from-rose-100 to-rose-50 px-3 py-2 shadow-[0_8px_14px_rgba(244,63,94,0.16)]">
                        <p className="text-[10px] font-black uppercase tracking-[0.12em] text-rose-600">Penalty</p>
                        <p className="mt-1 font-black text-rose-800">{currency} {penaltyAmount.toFixed(2)}</p>
                      </div>
                      <div className="rounded-2xl border border-emerald-300/80 bg-gradient-to-b from-emerald-100 to-emerald-50 px-3 py-2 shadow-[0_8px_14px_rgba(16,185,129,0.16)]">
                        <p className="text-[10px] font-black uppercase tracking-[0.12em] text-emerald-600">Refundable</p>
                        <p className="mt-1 font-black text-emerald-800">{currency} {refundableAmount.toFixed(2)}</p>
                      </div>
                    </div>
                    <div className="rounded-2xl border border-slate-200 bg-gradient-to-b from-slate-100 to-white px-3 py-2 text-xs text-slate-700 shadow-[inset_0_1px_0_rgba(255,255,255,0.95)]">
                      Current window: <span className="font-black">{windowText}</span> • Policy source: <span className="font-black">System Registry</span>
                    </div>
                  </>
                );
              })()}

              <div className="space-y-1">
                <label className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Cancellation Reason (Required)</label>
                <textarea
                  value={cancelReason}
                  onChange={(e) => setCancelReason(e.target.value)}
                  className="w-full min-h-[90px] rounded-2xl border border-slate-300 bg-gradient-to-b from-white to-slate-50 px-3 py-2 text-sm shadow-[inset_0_1px_0_rgba(255,255,255,0.95),0_6px_12px_rgba(15,23,42,0.06)] outline-none focus:border-rose-300 focus:ring-2 focus:ring-rose-100"
                  placeholder="Reason for cancellation"
                />
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-slate-200 bg-slate-50/80">
              <button
                type="button"
                onClick={() => {
                  setShowCancelConfirm(false);
                  setCancelReason('');
                }}
                className="rounded-full border border-slate-300 bg-white px-4 py-2 text-[10px] font-black uppercase tracking-[0.12em] text-slate-700 shadow-sm transition hover:bg-slate-50"
              >
                Keep Ticket
              </button>
              <button
                type="button"
                onClick={() => void confirmCancelTicket()}
                disabled={isConfirmingCancel}
                className="rounded-full border border-rose-300/80 bg-gradient-to-b from-rose-500 to-rose-600 px-4 py-2 text-[10px] font-black uppercase tracking-[0.12em] text-white shadow-[0_10px_20px_rgba(244,63,94,0.28)] transition hover:brightness-105 disabled:opacity-60"
              >
                {isConfirmingCancel ? 'Cancelling...' : 'Confirm Cancel'}
              </button>
            </div>
          </div>
        </div>
      )}

      {ticketPlan && (
        <div className="fixed inset-0 z-[130] bg-slate-900/45 backdrop-blur-sm p-4 flex items-center justify-center">
          <div className="w-full max-w-2xl rounded-2xl border border-slate-200 bg-white shadow-2xl overflow-hidden">
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
              <div>
                <p className="text-[10px] font-black uppercase tracking-[0.14em] text-slate-500">Issue Ticket</p>
                <h4 className="text-base font-black text-slate-900">{ticketPlan.fromCode} ➔ {ticketPlan.toCode}</h4>
              </div>
              <button onClick={() => setTicketPlan(null)} className="rounded-lg border border-slate-300 px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-slate-700">Close</button>
            </div>
            <div className="p-4 space-y-4">
              <div className="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={() => setTicketMode('HELD')}
                  className={`rounded-lg border px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.12em] ${ticketMode === 'HELD' ? 'border-amber-300 bg-amber-50 text-amber-700' : 'border-slate-200 bg-white text-slate-600'}`}
                >
                  Hold
                </button>
                <button
                  type="button"
                  onClick={() => setTicketMode('CONFIRMED')}
                  className={`rounded-lg border px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.12em] ${ticketMode === 'CONFIRMED' ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-slate-200 bg-white text-slate-600'}`}
                >
                  Confirm
                </button>
                <span className={`ml-auto inline-flex rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] ${ticketMode === 'HELD' ? 'border-amber-200 bg-amber-50 text-amber-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
                  {ticketMode === 'HELD' ? 'Seat Hold Mode' : 'Direct Confirm Mode'}
                </span>
              </div>
              <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Flight Snapshot</p>
              <div className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800">
                Departure: {fmtDateTime(ticketPlan.departureAt)} · Available: {ticketPlan.availableSeats} · Rack Unit: {ticketPlan.currencyCode} {ticketPricing.rackUnit.toFixed(2)} · Net Unit: {ticketPlan.currencyCode} {ticketPricing.netUnitPrice.toFixed(2)}
              </div>
              <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Booking Window</p>
              <div className={`rounded-lg border px-3 py-2 text-xs ${ticketBookingWindow.isClosed ? 'border-rose-200 bg-rose-50 text-rose-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700'}`}>
                Booking allowed until: {ticketBookingWindow.cutoffAt ? fmtDateTime(ticketBookingWindow.cutoffAt.toISOString()) : '—'}
                {ticketBookingWindow.minHoursBeforeDeparture > 0
                  ? ` (${ticketBookingWindow.minHoursBeforeDeparture}h before departure)`
                  : ' (until departure)'}
              </div>
              {ticketBookingWindow.isClosed && (
                <div className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                  Manual booking window is closed for this flight. Authorized roles can override with reason.
                </div>
              )}
              {Number(ticketForm.unitPriceUSD || 0) <= 0 && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                  Price is zero. Set unit price before issuing ticket.
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                <div className="md:col-span-2 flex items-center justify-between">
                  <label className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Customer Source</label>
                  <button
                    type="button"
                    onClick={() => setShowQuickCreate(true)}
                    className="text-[10px] text-indigo-600 hover:text-indigo-700 font-semibold flex items-center gap-1"
                  >
                    + New Contact
                  </button>
                </div>
                <select
                  className="md:col-span-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                  value={ticketForm.selectedCustomerId}
                  onChange={(e) => {
                    const nextId = e.target.value;
                    if (nextId === 'MANUAL') {
                      setTicketForm((v) => ({
                        ...v,
                        selectedCustomerId: 'MANUAL',
                        customerId: '',
                      }));
                      return;
                    }
                    const found = mergedCustomers.find((c) => c.id === nextId);
                    setTicketForm((v) => ({
                      ...v,
                      selectedCustomerId: nextId,
                      customerId: found?.id || '',
                      customerName: found?.name || v.customerName,
                      customerEmail: found?.email || v.customerEmail,
                    }));
                  }}
                >
                  <option value="MANUAL">Manual customer entry</option>
                  {localCustomers.map((c) => (
                    <option key={c.id} value={c.id}>
                      ★ {c.name}{c.email ? ` · ${c.email}` : ''}
                    </option>
                  ))}
                  {customers
                    .filter((c) => c.status === 'ACTIVE' || c.status === 'LEAD' || c.status === 'PROSPECT')
                    .map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}{c.email ? ` · ${c.email}` : ''}
                      </option>
                    ))}
                </select>
                <div className="md:col-span-2 flex flex-wrap items-center gap-1.5">
                  <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] ${ticketIdentityLinked ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-rose-200 bg-rose-50 text-rose-700'}`}>
                    {ticketIdentityLinked ? 'Linked' : 'Unlinked'}
                  </span>
                  <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] ${ticketIdentityLinked ? 'border-sky-200 bg-sky-50 text-sky-700' : 'border-amber-200 bg-amber-50 text-amber-700'}`}>
                    {ticketIdentityLinked ? 'Known Contact' : 'Needs Verification'}
                  </span>
                  {ticketIdentityLinked ? (
                    <span className={`inline-flex rounded-full border px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.12em] ${ticketZohoLinked ? 'border-indigo-200 bg-indigo-50 text-indigo-700' : 'border-amber-200 bg-amber-50 text-amber-700'}`}>
                      {ticketZohoLinked ? 'Identity Linked' : 'Needs Link'}
                    </span>
                  ) : null}
                </div>
                {ticketForm.selectedCustomerId !== 'MANUAL' && (
                  <div className="md:col-span-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-600">
                    {isLoadingTicketCustomer
                      ? 'Loading customer commercial terms...'
                      : `Effective schedule discount: ${customerDiscountPercent}% (source: ${customerDiscountSource})`}
                  </div>
                )}
                <label className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Customer Name</label>
                <label className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Customer Email</label>
                <input
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                  placeholder="Customer Name *"
                  value={ticketForm.customerName}
                  onChange={(e) => setTicketForm((v) => ({ ...v, customerName: e.target.value }))}
                />
                <input
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                  placeholder="Customer Email"
                  value={ticketForm.customerEmail}
                  onChange={(e) => setTicketForm((v) => ({ ...v, customerEmail: e.target.value }))}
                />
                <label className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Customer ID</label>
                <label className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Seats</label>
                <input
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                  placeholder="Customer ID (optional)"
                  value={ticketForm.customerId}
                  onChange={(e) => setTicketForm((v) => ({ ...v, customerId: e.target.value }))}
                />
                <input
                  type="number"
                  min={1}
                  max={Math.max(1, Number(ticketPlan.availableSeats || 1))}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                  placeholder="Seats"
                  value={ticketForm.seats}
                  onChange={(e) => setTicketForm((v) => ({ ...v, seats: Math.max(1, Number(e.target.value) || 1) }))}
                />
                <label className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Rack Unit Price (USD)</label>
                <label className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Rack Total</label>
                <input
                  type="number"
                  min={0}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                  placeholder="Rack Unit Price (USD)"
                  value={ticketForm.unitPriceUSD}
                  onChange={(e) => setTicketForm((v) => ({ ...v, unitPriceUSD: Math.max(0, Number(e.target.value) || 0) }))}
                />
                <input
                  className="rounded-lg border border-slate-200 bg-slate-100 px-3 py-2 text-sm"
                  value={`Rack Total: ${ticketPricing.rackTotal.toLocaleString()}`}
                  readOnly
                />
                <label className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Operator Discount (%)</label>
                <label className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Net Total</label>
                <input
                  type="number"
                  min={0}
                  max={Math.max(0, Number(operatorDiscountCap || 0))}
                  className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                  placeholder={`Operator Discount % (max ${operatorDiscountCap})`}
                  value={ticketForm.operatorDiscountPercent}
                  onChange={(e) =>
                    setTicketForm((v) => ({
                      ...v,
                      operatorDiscountPercent: Math.max(0, Math.min(Number(e.target.value) || 0, Number(operatorDiscountCap || 0))),
                    }))
                  }
                />
                <input
                  className="rounded-lg border border-slate-200 bg-slate-100 px-3 py-2 text-sm"
                  value={`Net Total: ${ticketPricing.netTotal.toLocaleString()}`}
                  readOnly
                />
                {ticketMode === 'HELD' && (
                  <label className="md:col-span-2 text-[10px] font-black uppercase tracking-[0.12em] text-amber-600">Hold Until</label>
                )}
                {ticketMode === 'HELD' && (
                  <input
                    type="datetime-local"
                    className="md:col-span-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm"
                    value={ticketForm.holdUntil}
                    onChange={(e) => setTicketForm((v) => ({ ...v, holdUntil: e.target.value }))}
                  />
                )}
                {ticketBookingWindow.isClosed && (
                  <>
                    <label className="text-[10px] font-black uppercase tracking-[0.12em] text-rose-600">Cutoff Override</label>
                    <label className="text-[10px] font-black uppercase tracking-[0.12em] text-rose-600">Override Reason</label>
                    <label className="flex items-center rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
                      <input
                        type="checkbox"
                        className="mr-2"
                        checked={ticketForm.cutoffOverride}
                        onChange={(e) => setTicketForm((v) => ({ ...v, cutoffOverride: e.target.checked }))}
                      />
                      Authorized override
                    </label>
                    <input
                      className="rounded-lg border border-rose-200 bg-white px-3 py-2 text-sm"
                      placeholder="Reason for override (mandatory)"
                      value={ticketForm.cutoffOverrideReason}
                      onChange={(e) => setTicketForm((v) => ({ ...v, cutoffOverrideReason: e.target.value }))}
                    />
                  </>
                )}
                <label className="md:col-span-2 text-[10px] font-black uppercase tracking-[0.12em] text-slate-500">Notes</label>
                <textarea
                  className="md:col-span-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm min-h-[90px]"
                  placeholder="Notes"
                  value={ticketForm.notes}
                  onChange={(e) => setTicketForm((v) => ({ ...v, notes: e.target.value }))}
                />
              </div>
              <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
                <div className="mb-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-[11px]">
                  <div className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-emerald-800">
                    Customer Discount: {customerDiscountPercent}% (-{ticketPricing.customerDiscountAmount.toFixed(2)})
                  </div>
                  <div className="rounded-md border border-indigo-200 bg-indigo-50 px-2 py-1 text-indigo-800">
                    Operator Discount: {ticketPricing.cappedOperatorDiscountPercent}% (-{ticketPricing.operatorDiscountAmount.toFixed(2)})
                  </div>
                </div>
                <p className="text-[10px] font-black uppercase tracking-[0.12em] text-slate-500 mb-2">
                  Passenger Details ({ticketPassengers.length})
                </p>
                <div className="space-y-2 max-h-56 overflow-auto pr-1">
                  {ticketPassengers.map((pax, idx) => (
                    <div key={`pax-${idx + 1}`} className="grid grid-cols-1 md:grid-cols-[1fr_220px] gap-2">
                      <input
                        className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                        placeholder={`Passenger ${idx + 1} Name *`}
                        value={pax.name}
                        onChange={(e) =>
                          setTicketPassengers((prev) =>
                            prev.map((row, i) => (i === idx ? { ...row, name: e.target.value } : row))
                          )
                        }
                      />
                      <input
                        className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm"
                        placeholder="Passport/ID (optional)"
                        value={pax.idNumber}
                        onChange={(e) =>
                          setTicketPassengers((prev) =>
                            prev.map((row, i) => (i === idx ? { ...row, idNumber: e.target.value } : row))
                          )
                        }
                      />
                    </div>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setTicketPlan(null)}
                  className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-[10px] font-black uppercase tracking-[0.12em] text-slate-700"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submitIssueTicket}
                  disabled={isIssuingTicket || (ticketBookingWindow.isClosed && !ticketForm.cutoffOverride)}
                  className="rounded-lg border border-emerald-300 bg-emerald-500 px-3 py-2 text-[10px] font-black uppercase tracking-[0.12em] text-white disabled:opacity-60"
                >
                  {isIssuingTicket
                      ? (ticketMode === 'HELD' ? 'Holding...' : 'Issuing...')
                      : (ticketMode === 'HELD'
                          ? (ticketBookingWindow.isClosed ? 'Hold Seat (Override)' : 'Hold Seat')
                          : (ticketBookingWindow.isClosed ? 'Confirm (Override)' : 'Confirm Ticket'))}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {showQuickCreate && (
        <FullContactFormModal
          onSaved={(c) => {
            setLocalCustomers((prev) => [c, ...prev]);
            setTicketForm((v) => ({
              ...v,
              selectedCustomerId: c.id,
              customerId: c.id,
              customerName: c.name,
              customerEmail: c.email || v.customerEmail,
            }));
            setShowQuickCreate(false);
          }}
          onClose={() => setShowQuickCreate(false)}
        />
      )}
    </>
  );
};

export default ScheduledCommandPanelModals;
